import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { Box } from '@mui/material';

import HierarchyBreadcrumbs from '../../components/core/HierarchyBreadcrumbs';
import { SaveBar } from '../../common';
import { SAVE_BAR_HEIGHT } from '../../common/SaveBar/SaveBar';
import { agentHubPaths } from '../../util/constants';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';
import { AgentHubOutletContext } from './agentHubOutletContext';
import { useMerchant } from '../../contexts/MerchantContext';
import {
  usePatchAgenticSettingsMutation,
  usePostAgenticSettingsMutation,
} from '../../store/modules/merchant/api';
import { toast } from '../../util/toast';
import { EllaDraftProvider, useEllaDraft } from './EllaDraftContext';
import { AGENT_HUB_CONTENT_MAX_WIDTH } from './ellaFormStyles';
import { AgentHubPageHeader } from './components/AgentHubPageHeader';
import { getAgentHubPageHeaderForPath } from './agentHubNavItems';
import { useProvisioningPlatform } from './provisioning';

const getSaveErrorMessage = (err: unknown, fallback: string): string => {
  const data = (err as { data?: { errors?: string[]; message?: string | string[] } })?.data;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.join(' ');
  }
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }
  if (Array.isArray(data?.message) && data.message.length > 0) {
    return data.message.join(' ');
  }
  return fallback;
};

const pagePaddingSx = {
  pl: { xs: 8, md: 13, lg: 15 },
  pr: { xs: 5, sm: 8, md: 13, lg: 15 },
};

const AgentHubSaveBar = () => {
  const navigate = useMerchantAwareNavigate();
  const { merchantId } = useMerchant();
  const { onCancelEnableElla, onEnableSuccess } = useOutletContext<AgentHubOutletContext>();
  const {
    createMode,
    readOnly,
    isDirty,
    isValid,
    resetDraft,
    commitDraft,
    getCreatePayload,
    getPatchPayload,
  } = useEllaDraft();
  const [postAgenticSettings, { isLoading: isPosting }] = usePostAgenticSettingsMutation();
  const [patchAgenticSettings, { isLoading: isPatching }] = usePatchAgenticSettingsMutation();
  const isSaving = isPosting || isPatching;

  const handleCancel = () => {
    if (createMode) {
      onCancelEnableElla();
      navigate(agentHubPaths.DASHBOARD);
      return;
    }
    resetDraft();
  };

  const handleSave = async () => {
    if (!merchantId || !isValid) {
      return;
    }

    if (createMode) {
      try {
        await postAgenticSettings({
          merchantId,
          body: { agents: { Ella: getCreatePayload() } },
        }).unwrap();
        onEnableSuccess();
        toast.success('Agent Hub enabled successfully');
      } catch (err) {
        toast.error(getSaveErrorMessage(err, 'Failed to enable Agent Hub'));
      }
      return;
    }

    const body = getPatchPayload();
    if (Object.keys(body).length === 0) {
      return;
    }

    try {
      const result = await patchAgenticSettings({
        merchantId,
        agentName: 'Ella',
        body,
      }).unwrap();
      commitDraft(result.agents?.Ella);
      toast.success('Agent settings saved');
    } catch (err) {
      toast.error(getSaveErrorMessage(err, 'Failed to save agent settings'));
    }
  };

  return (
    <SaveBar
      show={(createMode || isDirty) && !readOnly}
      saveLoading={isSaving}
      onSave={() => {
        void handleSave();
      }}
      onCancel={handleCancel}
      text={isSaving ? 'Saving current changes...' : 'Save current changes?'}
      disableSave={isSaving || !isValid || (!createMode && !isDirty)}
      dataTestId="ella-agent-hub-save-bar"
    />
  );
};

const AgentHubChrome = () => {
  const location = useLocation();
  const { createMode, isDirty, readOnly: draftReadOnly } = useEllaDraft();
  const { platform: provisioningPlatform } = useProvisioningPlatform();
  const tabContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = tabContentRef.current;
    if (!el) {
      return;
    }
    el.scrollTop = 0;
  }, [location.pathname]);

  const showSaveBar = (createMode || isDirty) && !draftReadOnly;
  const pageHeader = getAgentHubPageHeaderForPath(location.pathname, provisioningPlatform);

  return (
    <Box
      component="section"
      data-testid="ELLA_AGENT_HUB"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        mt: 3,
      }}
    >
      <Box
        sx={{
          ...pagePaddingSx,
          flexShrink: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Box sx={{ maxWidth: AGENT_HUB_CONTENT_MAX_WIDTH, width: '100%' }}>
          <HierarchyBreadcrumbs />
          {pageHeader && (
            <Box sx={{ mt: { xs: 2, md: 3 } }}>
              <AgentHubPageHeader title={pageHeader.label} description={pageHeader.description} />
            </Box>
          )}
        </Box>
      </Box>

      <Box
        ref={tabContentRef}
        sx={{
          ...pagePaddingSx,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pt: 0,
          pb: showSaveBar ? `${SAVE_BAR_HEIGHT + 16}px` : 3,
        }}
      >
        <Box sx={{ maxWidth: AGENT_HUB_CONTENT_MAX_WIDTH, width: '100%' }}>
          <Outlet />
        </Box>
      </Box>

      {showSaveBar && <AgentHubSaveBar />}
    </Box>
  );
};

export const AgentHub = () => {
  const { canUpdateAgentHub } = useMerchant();
  const { isEnablingElla, settingsMissing } = useOutletContext<AgentHubOutletContext>();

  const createMode = settingsMissing && isEnablingElla;
  const readOnly = !canUpdateAgentHub();

  return (
    <EllaDraftProvider createMode={createMode} readOnly={readOnly}>
      <AgentHubChrome />
    </EllaDraftProvider>
  );
};
